import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, ButtonDropdown, ExpandableSection, Link, Popover, SpaceBetween, Spinner, StatusIndicator,
} from '@amzn/awsui-components-react';
import { useLocation, useParams } from 'react-router-dom';
import 'src/assets/styles/steps-wizard.scss';
import ServiceCollection from 'src/services/ServiceCollection';
import { GlobalAppContext } from 'src/components/App';
import { GLOBAL_ACTIONS } from 'src/services/global/GlobalReducer';
import DynamicBreadcrumb from 'src/models/navigation/DynamicBreadcrumb';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import CONSTANTS from 'src/utils/constants';
import DateUtils from 'src/utils/dateUtils';
import ErrorUtils from 'src/utils/ErrorUtils';
import StepsWizardBar from 'src/components/shared/StepsWizardBar';
import WizardStepContentWrapper from 'src/components/calculation-builder/WizardStepContentWrapper';
import { WizardStep } from 'src/models/common/WizardStep';
import { STATUS_INDICATOR_CONFIG } from 'src/components/multidimensional-allocation/common/Constants';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { DerivedAllocationSheetData } from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { ACTIONS, derivedAllocationSheetBuilderReducer } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import { deriveDasViewMode, initialState } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderState';
import DasDataSetSection from './DasDataSetSection';
import DasTotalsSection from './DasTotalsSection';
import DasAllocationGroupsSection from './DasAllocationGroupsSection';
import DasMappingSection from './DasMappingSection';
import DasSheetActions from './DasSheetActions';
import useDasExecution from './useDasExecution';
import { executeBlockedReason } from 'src/services/derived-allocation-sheet/dasExecution';
import {
    EditLockButtonBar, isBlockedByEditLock, isLockedByOther, LockBanner, LOCK_COMPONENT_TYPES, useLockActions,
} from 'src/components/shared/edit-locking';
import { EDIT_LOCKING_FLAG, getFeatureFlags, isFeatureEnabledForUser } from 'src/services/FeatureFlagsService';

export const DAS_STEP_IDS = ['das-step-1', 'das-step-2', 'das-step-3', 'das-step-4'] as const;

const STEP_NAMES = ['Configure data set', 'Configure totals', 'Configure allocation groups', 'Segment to group mapping'];

export const EXECUTE_LOCK_REASON = 'Click Edit to acquire the lock before executing.';
export const SUBMIT_LOCK_REASON = 'Click Edit to acquire the lock before submitting.';

/**
 * Names a list of steps for a message, e.g. "Step 1" or "Step 1 and Step 3".
 * @param steps The step numbers.
 * @returns The step names in step order.
 */
export function stepNames(steps: number[]): string {
    const names = [...steps].sort((a, b) => a - b).map((n) => `Step ${n}`);
    return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const scrollToDasStep = (index: number) => {
    const element = document.getElementById(DAS_STEP_IDS[index]);
    if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

function SheetStatus({ status }: { status: string }) {
    const config = STATUS_INDICATOR_CONFIG[status as WorksheetStatus];
    if (!config) return <>{status}</>;
    const [label, type, color] = config;
    return <StatusIndicator data-class="statusText" type={type} colorOverride={color}>{label}</StatusIndicator>;
}

/**
 * Submit and review details shown behind the status Info link.
 * @param sheet The sheet whose details are shown.
 * @returns The popover content.
 */
function statusInfoContent(sheet: DerivedAllocationSheetData): React.ReactNode {
    if (!sheet.submitUser) {
        return <span className="statusInfoItem">{sheet.comment || ''}</span>;
    }
    const commenter = sheet.status === 'PENDING_APPROVAL' ? 'Submitter' : 'Reviewer';
    return (
        <ul>
            <li className="statusInfoItem"><b>Submitted by: </b>{sheet.submitUser}</li>
            <li className="statusInfoItem"><b>Submitted on: </b>{sheet.submitDate}</li>
            {!!sheet.reviewUser && <li className="statusInfoItem"><b>Reviewed by: </b>{sheet.reviewUser}</li>}
            {!!sheet.reviewUser && <li className="statusInfoItem"><b>Reviewed on: </b>{sheet.reviewDate}</li>}
            {!!sheet.comment && <li className="statusInfoItem"><b>{commenter} comment: </b>{sheet.comment}</li>}
        </ul>
    );
}

/** Run statuses the status bar shows. */
const SHOWN_EXECUTION_STATUSES: string[] = [
    CONSTANTS.PROCESSING_STATUS.SUCCEEDED, CONSTANTS.PROCESSING_STATUS.FAILED, CONSTANTS.PROCESSING_STATUS.IN_PROGRESS,
];

/**
 * Outcome of the last execution, as the TPA worksheet shows it after "Last executed": SUCCEEDED, FAILED with its
 * reason behind a popover, or IN PROGRESS. Takes one of {@link SHOWN_EXECUTION_STATUSES}.
 */
function DasExecutionStatus({ status, reason }: { status: string, reason?: string }) {
    switch (status) {
        case CONSTANTS.PROCESSING_STATUS.SUCCEEDED:
            return <span data-class="dasExecutionStatus" className="executionStatusSuccess">{status}</span>;
        case CONSTANTS.PROCESSING_STATUS.FAILED:
            return (
                <span data-class="dasExecutionStatus" className="executionStatusFailed">
                    {reason
                        ? <Popover data-class="basicPopover" dismissButton={false} position="top" content={reason}><i>{status}</i></Popover>
                        : status}
                </span>
            );
        default:
            return <span data-class="dasExecutionStatus" className="executionStatusInProgress">{status}</span>;
    }
}

/**
 * Name of a sheet version in the version history.
 * @param version The version: -1 is the draft, 0 the live version, 1 and up the earlier live versions.
 * @returns The name shown for the version.
 */
export function dasVersionLabel(version: number): string {
    if (version === CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION) return 'Draft';
    if (version === CONSTANTS.CALCULATION_VERSIONS.CURRENT_VERSION) return 'Live version';
    return `Version ${version}`;
}

/**
 * Version history menu, as the TPA worksheet's past execution results menu: it opens one version of the sheet
 * read-only, and Clear Selection goes back to the working version.
 */
function DasVersionHistory(props: {
    sheet: DerivedAllocationSheetData,
    availableVersions: number[],
    viewingWorkingVersion: boolean,
    switching: boolean,
    /** True while a step has unsaved rows or a save in flight, or a run is in progress. */
    hasUnsavedWork: boolean,
    onOpenVersion: (version: number | null) => void,
}) {
    const { sheet, availableVersions, viewingWorkingVersion, switching, hasUnsavedWork, onOpenVersion } = props;
    // The draft first, then the live version, then earlier live versions from newest to oldest.
    const versions = [...availableVersions].sort((a, b) => (a <= 0 || b <= 0 ? a - b : b - a));
    const items = [
        { id: CONSTANTS.CLEAR_SELECTION, text: CONSTANTS.CLEAR_SELECTION, disabled: viewingWorkingVersion },
        ...versions.map((version) => ({
            id: String(version), text: dasVersionLabel(version), disabled: version === sheet.sheetVersion,
        })),
    ];
    let label = 'View version history';
    if (switching) label = 'Loading version...';
    else if (!viewingWorkingVersion) label = dasVersionLabel(sheet.sheetVersion);
    return (
        <ButtonDropdown data-class="dasVersionHistory" className="polarisSmallButtonDropdown" items={items}
                        disabled={switching || hasUnsavedWork || versions.length < 2}
                        onItemClick={({ detail }) => onOpenVersion(detail.id === CONSTANTS.CLEAR_SELECTION ? null : Number(detail.id))}>
            {label}
        </ButtonDropdown>
    );
}

/**
 * Execute button, as the TPA worksheet's: it runs the draft and shows the run in progress.
 */
function DasExecuteButton(props: { executing: boolean, blockedReason?: string, onExecute: () => void }) {
    const { executing, blockedReason, onExecute } = props;
    return (
        <Button data-class="dasExecuteButton" variant="primary" onClick={onExecute} loading={executing}
                loadingText="Executing..." disabled={executing || !!blockedReason} disabledReason={blockedReason}>
            {executing ? 'Executing...' : 'Execute sheet'}
        </Button>
    );
}

/**
 * Status bar under the steps: the sheet status with its review details, the last execution time, the
 * version history and, for users who can edit the working version, the Execute button and the edit lock buttons.
 */
function DasStatusBar(props: {
    sheet: DerivedAllocationSheetData,
    canEdit: boolean,
    versionHistory: React.ReactNode,
    /** Shown when set. */
    executeButton?: React.ReactNode,
    /** Edit, Save, Revert and Confirm, shown when set. */
    lockActions?: React.ReactNode,
    /** Set when a version other than the working one is open. */
    pastVersionLabel?: string,
    /** True while a run of the draft is in progress; the sheet shows the run's outcome once it is reread. */
    executing: boolean,
}) {
    const { sheet, canEdit, versionHistory, executeButton, lockActions, pastVersionLabel, executing } = props;
    // The run on this page while it lasts, otherwise the stored outcome; an unknown status shows nothing.
    const runStatus = executing ? CONSTANTS.PROCESSING_STATUS.IN_PROGRESS : sheet.executionStatus;
    const shownStatus = runStatus && SHOWN_EXECUTION_STATUSES.includes(runStatus) ? runStatus : undefined;
    return (
        <>
            <Box padding={{ left: 'n', right: 'l', top: 'xl', bottom: 's' }}>
                <div className="entityStatusBar" data-class="dasStatusBar">
                    <div className="topMarginDiv entityStatusBarElement">
                        <SpaceBetween size="s" direction="horizontal">
                            <strong>Sheet status: </strong>
                            <div>
                                <SheetStatus status={sheet.status} />
                                {(!!sheet.comment || !!sheet.submitUser) && (
                                    <sub>
                                        <Popover dismissButton={false} position="top" triggerType="custom" content={statusInfoContent(sheet)}>
                                            <Link data-class="statusReasonInfoLink" variant="info">Info</Link>
                                        </Popover>
                                    </sub>
                                )}
                            </div>
                        </SpaceBetween>
                    </div>
                    <div className="topMarginDiv verticalSeparator entityStatusBarElement">
                        <SpaceBetween size="s" direction="horizontal">
                            <strong>Last executed:</strong>
                            <div>
                                {executing && !sheet.lastExecutionTimestamp && <DasExecutionStatus status={CONSTANTS.PROCESSING_STATUS.IN_PROGRESS} />}
                                {!executing && !sheet.lastExecutionTimestamp && <span className="notExecutedText">Not executed</span>}
                                {!!sheet.lastExecutionTimestamp && (
                                    <>
                                        <span>
                                            {DateUtils.formatDate(sheet.lastExecutionTimestamp, 'YYYY/MM/DD hh:mm a')}
                                            {sheet.lastExecutionUser ? ` by ${sheet.lastExecutionUser}` : ''}
                                        </span>
                                        {!!shownStatus && (
                                            <>
                                                <span> (</span>
                                                <DasExecutionStatus status={shownStatus} reason={sheet.executionStatusReason} />
                                                <span>)</span>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </SpaceBetween>
                    </div>
                    <div className="verticalSeparator entityStatusBarElement">
                        <Box float="right">{versionHistory}</Box>
                    </div>
                    {(executeButton || lockActions) && (
                        <div className="entityStatusBarElement executeWorksheetDiv">
                            <Box float="right" textAlign="center">
                                <SpaceBetween size="s" direction="horizontal">
                                    {executeButton}
                                    {lockActions}
                                </SpaceBetween>
                            </Box>
                        </div>
                    )}
                </div>
            </Box>
            {pastVersionLabel && (
                <div className="subtleAlert">
                    <StatusIndicator data-class="versionAlertText" type="info">
                        {`Viewing ${pastVersionLabel}, which is read-only. Select ${CONSTANTS.CLEAR_SELECTION} in the version history to go back.`}
                    </StatusIndicator>
                </div>
            )}
            {!pastVersionLabel && canEdit && sheet.status === 'PENDING_APPROVAL' && (
                <div className="subtleAlert">
                    <StatusIndicator data-class="alertText" type="info">This sheet is under review and cannot be modified.</StatusIndicator>
                </div>
            )}
        </>
    );
}

/**
 * Derived allocation sheet builder. On first open it reads the working version (the draft when there is
 * one, else the live version); later refreshes reread the version that was opened. The page title comes
 * from the app header; the page shows the step bar, the status bar, the four step sections and, under them, the
 * sheet actions, as the TPA worksheet does. Step 1 opens expanded; selecting a step in the step bar expands it
 * and scrolls to it.
 */
export default function DerivedAllocationSheetBuilder({ services }: { services: ServiceCollection }) {
    const { sheetId } = useParams<{ sheetId: string }>();
    const { pathname } = useLocation();
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);
    const globalContext = useContext(GlobalAppContext);
    const globalDispatch = globalContext?.globalDispatch;
    const currentUser = globalContext?.userProfile?.user || '';
    // Null until the feature flag loads.
    const [editLockingEnabled, setEditLockingEnabled] = useState<boolean | null>(null);
    const [dasState, dasDispatch] = useReducerWithLogger(derivedAllocationSheetBuilderReducer, initialState);
    const { sheet, loading, error, viewMode, sheetRefreshToken, executing, lockInfo } = dasState;
    const openedVersion = useRef<{ sheetId: string, version: number } | null>(null);
    // A version picked in the version history; null asks for the working version.
    const requestedVersion = useRef<{ version: number | null } | null>(null);
    const [versionRequests, setVersionRequests] = useState(0);
    const [viewingWorkingVersion, setViewingWorkingVersion] = useState(true);
    const [switchingVersion, setSwitchingVersion] = useState(false);
    const [selectedStep, setSelectedStep] = useState(1);
    const [expandedSteps, setExpandedSteps] = useState<number[]>([1]);
    // Set by a lock action or an approval so the next read remounts the steps: they can change the rows
    // without changing the version that is open.
    const reloadSteps = useRef(false);
    const [stepsGeneration, setStepsGeneration] = useState(0);

    useEffect(() => {
        let active = true;
        const opened = openedVersion.current?.sheetId === sheetId ? openedVersion.current.version : null;
        // A picked version stays requested until a read of it settles, so a refresh during the read reads it again.
        const requested = opened == null ? null : requestedVersion.current;
        if (opened == null) requestedVersion.current = null;
        setSwitchingVersion(requested != null);
        const settle = () => {
            if (requested != null && requestedVersion.current === requested) requestedVersion.current = null;
        };
        const service = services.derivedAllocationSheetService;
        if (opened == null) {
            // A different sheet is opened: drop the previous one so the spinner shows instead of its page.
            setSelectedStep(1);
            setExpandedSteps([1]);
            setViewingWorkingVersion(true);
            if (openedVersion.current != null) {
                openedVersion.current = null;
                dasDispatch(ACTIONS.SET_SHEET_RESULT.withPayload({ sheet: null, availableVersions: [] }));
            }
            dasDispatch(ACTIONS.SET_LOADING.withPayload(true));
        }
        const reportFailure = (message: string) => {
            if (opened == null) {
                dasDispatch(ACTIONS.SET_ERROR.withPayload(message));
            } else {
                services.messageService.showErrorBanner(message);
            }
        };
        const target = requested ? requested.version : opened;
        (target == null ? service.getWorkingDerivedAllocationSheet({ sheetId }) : service.getDerivedAllocationSheetAt({ sheetId, version: target }))
            .then(({ sheetData, availableVersions, lockInfo: readLockInfo }) => {
                if (!active) return;
                settle();
                if (sheetData == null) {
                    reportFailure(`Derived allocation sheet ${sheetId} was not found.`);
                } else {
                    openedVersion.current = { sheetId, version: sheetData.sheetVersion };
                    if (requested) setViewingWorkingVersion(requested.version == null);
                    dasDispatch(ACTIONS.SET_SHEET_RESULT.withPayload({ sheet: sheetData, availableVersions, lockInfo: readLockInfo }));
                    if (reloadSteps.current) {
                        reloadSteps.current = false;
                        setStepsGeneration((n) => n + 1);
                    }
                }
            })
            .catch((ex) => {
                if (!active) return;
                settle();
                reportFailure(ErrorUtils.getMessage(ex));
            })
            .finally(() => {
                if (active) setSwitchingVersion(false);
            });
        return () => {
            active = false;
        };
    }, [sheetId, sheetRefreshToken, versionRequests]);

    /**
     * Opens one version from the version history, or the working version when `version` is null. The working
     * version is the draft when the sheet has one, else the live version, so picking it is the same as Clear Selection.
     */
    const openVersion = (version: number | null) => {
        const { availableVersions } = dasState;
        const workingVersion = availableVersions.includes(CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION)
            ? CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION : CONSTANTS.CALCULATION_VERSIONS.CURRENT_VERSION;
        requestedVersion.current = { version: version === workingVersion ? null : version };
        setVersionRequests((n) => n + 1);
    };

    /** Rereads the working version and remounts the steps, after an action that can change their rows. */
    const reloadWorkingVersion = () => {
        reloadSteps.current = true;
        openVersion(null);
    };

    useEffect(() => {
        let cancelled = false;
        getFeatureFlags()
            .then((flags) => {
                if (!cancelled) setEditLockingEnabled(isFeatureEnabledForUser(flags, EDIT_LOCKING_FLAG, currentUser));
            })
            .catch((err) => {
                console.error('Failed to load edit-locking feature flag', err);
                if (!cancelled) setEditLockingEnabled(false);
            });
        return () => { cancelled = true; };
    }, [currentUser]);

    const { handleEdit, handleSave, handleConfirm, handleRevert, actionInProgress } = useLockActions({
        componentType: LOCK_COMPONENT_TYPES.DERIVED_MDA_SHEET,
        entityId: sheet?.sheetId,
        currentUser,
        dispatch: dasDispatch,
        setViewModeAction: ACTIONS.SET_LOCK_VIEW_MODE,
        messageService: services.messageService,
        // Edit can create the draft and Revert can remove it, so reread the working version.
        onRefetch: reloadWorkingVersion,
        messages: {
            editSuccess: 'You are now editing this sheet.',
            lockedByOther: (lockedBy: string) => `This sheet is locked by ${lockedBy}.`,
        },
    });

    /** Runs a lock action that keeps the draft, once every step has saved its rows. */
    const whenStepsSaved = (action: () => void) => () => {
        if (dasState.unsavedSteps.length > 0) {
            services.messageService.showWarningBanner(`Save or remove the unsaved rows in ${stepNames(dasState.unsavedSteps)} first.`);
            return;
        }
        action();
    };

    /** Runs Revert once no step has a save in flight. */
    const whenNoStepSaving = (action: () => void) => () => {
        if (dasState.savingSteps.length > 0) {
            services.messageService.showWarningBanner(`Wait for the changes in ${stepNames(dasState.savingSteps)} to finish saving.`);
            return;
        }
        action();
    };

    useEffect(() => {
        dasDispatch(ACTIONS.SET_VIEW_MODE.withPayload(deriveDasViewMode({
            canEdit, status: sheet?.status, sheetVersion: sheet?.sheetVersion, executing, editLockingEnabled, lockInfo, currentUser,
        })));
    }, [sheet, canEdit, executing, editLockingEnabled, lockInfo, currentUser]);

    const { execute } = useDasExecution({
        services, sheetId, executing, dasDispatch,
        isDraft: sheet?.sheetId === sheetId && sheet?.sheetVersion === CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION,
    });

    useEffect(() => {
        // Only the sheet the route points at may title the page; until it loads, the route default shows.
        if (!globalDispatch) return;
        const current = sheet && sheet.sheetId === sheetId ? sheet : null;
        globalDispatch(GLOBAL_ACTIONS.SET_DYNAMIC_BREADCRUMB.withPayload(
            current ? new DynamicBreadcrumb(pathname, current.sheetName, current.sheetName) : new DynamicBreadcrumb(pathname, '')));
    }, [sheet?.sheetId, sheet?.sheetName, sheetId, pathname, globalDispatch]);

    const contextValue = useMemo(
        () => ({ dasState, dasDispatch, services, sheetId }),
        [dasState, dasDispatch, sheetId],
    );

    const wizardSteps: WizardStep[] = STEP_NAMES.map((name, index) => ({
        number: index + 1,
        name,
        scrollToId: DAS_STEP_IDS[index],
        isEnabled: true,
        isSelected: index + 1 === selectedStep,
    }));

    /** Selects a step in the step bar, expands its section and scrolls to it. */
    const goToStep = (number: number) => {
        setSelectedStep(number);
        setExpandedSteps((steps) => (steps.includes(number) ? steps : [...steps, number]));
        scrollToDasStep(number - 1);
    };

    const renderStep = (number: number): React.ReactNode => {
        switch (number) {
            case 1:
                return <DasDataSetSection onNextStep={() => goToStep(2)} />;
            case 2:
                return <DasTotalsSection onNextStep={() => goToStep(3)} />;
            case 3:
                return <DasAllocationGroupsSection onNextStep={() => goToStep(4)} />;
            case 4:
                return <DasMappingSection />;
            default:
                return null;
        }
    };

    /** Opens or closes one step section. */
    const setStepExpanded = (number: number, expanded: boolean) => {
        setExpandedSteps((steps) => (expanded ? [...steps.filter((n) => n !== number), number] : steps.filter((n) => n !== number)));
    };

    let body: React.ReactNode;
    if (sheet) {
        const workingDraft = viewingWorkingVersion && sheet.status === 'DRAFT_IN_PROGRESS'
            && sheet.sheetVersion === CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION;
        // While a run is in progress the page is read-only for everyone, so the lock gate waits for it to end.
        const blockedByLock = workingDraft && !executing && isBlockedByEditLock(viewMode, editLockingEnabled === true);
        body = (
            <div className="stepsWizard">
                <SpaceBetween direction="vertical" size="m">
                    <div className="stepsStripContainer">
                        <StepsWizardBar
                            wizardSteps={wizardSteps}
                            selectedWizardStep={wizardSteps[selectedStep - 1]}
                            onStepChanged={(step) => goToStep(step.number)}
                        />
                    </div>
                    {editLockingEnabled === true && isLockedByOther(lockInfo, currentUser) && (
                        <div data-class="dasLockBanner">
                            <LockBanner lockInfo={lockInfo} currentUser={currentUser} entityLabel="sheet" />
                        </div>
                    )}
                    <DasStatusBar sheet={sheet} canEdit={canEdit} executing={executing && viewingWorkingVersion} versionHistory={
                        <DasVersionHistory sheet={sheet} availableVersions={dasState.availableVersions}
                                           viewingWorkingVersion={viewingWorkingVersion}
                                           switching={switchingVersion || actionInProgress != null}
                                           hasUnsavedWork={dasState.unsavedSteps.length > 0 || executing}
                                           onOpenVersion={openVersion} />
                    } executeButton={canEdit && viewingWorkingVersion && (
                        <DasExecuteButton executing={executing} onExecute={execute}
                                          blockedReason={blockedByLock ? EXECUTE_LOCK_REASON
                                              : executeBlockedReason(sheet, dasState.unsavedSteps.length > 0)} />
                    )} lockActions={canEdit && editLockingEnabled === true && viewingWorkingVersion && (
                        <div data-class="dasLockActions">
                            <EditLockButtonBar viewMode={viewMode} lockInfo={lockInfo} currentUser={currentUser}
                                               actionInProgress={actionInProgress} onEdit={handleEdit}
                                               onSave={whenStepsSaved(handleSave)} onConfirm={whenStepsSaved(handleConfirm)}
                                               onRevert={whenNoStepSaving(handleRevert)}
                                               editDisabled={sheet.status === 'PENDING_APPROVAL' || executing}
                                               editDisabledReason={executing ? 'Wait for the execution to finish.'
                                                   : 'This sheet is pending approval and cannot be edited.'} />
                        </div>
                    )} pastVersionLabel={viewingWorkingVersion ? undefined : dasVersionLabel(sheet.sheetVersion)} />
                    {wizardSteps.map((step) => (
                        <WizardStepContentWrapper key={step.scrollToId} wizardStep={step}>
                            <ExpandableSection data-class="polarisExpandableSection" variant="container"
                                               headerText={`Step ${step.number}: ${step.name}`}
                                               expanded={expandedSteps.includes(step.number)}
                                               onChange={({ detail }) => setStepExpanded(step.number, detail.expanded)}>
                                <React.Fragment key={`${sheet.sheetId}:${sheet.sheetVersion}:${dasState.stepsRevision}:${stepsGeneration}`}>
                                    {renderStep(step.number)}
                                </React.Fragment>
                            </ExpandableSection>
                        </WizardStepContentWrapper>
                    ))}
                    {/* Right-aligned without a float, so the row keeps its height and the padding leaves space below it. */}
                    <Box textAlign="right" padding={{ bottom: 'xxl' }}>
                        <Box display="inline-block" data-class="dasSheetActionBar">
                            <DasSheetActions sheet={sheet} canEdit={canEdit} viewingWorkingVersion={viewingWorkingVersion}
                                             submitLockReason={blockedByLock ? SUBMIT_LOCK_REASON : undefined}
                                             onApprovalProcessed={reloadWorkingVersion} />
                        </Box>
                    </Box>
                </SpaceBetween>
            </div>
        );
    } else if (loading) {
        body = <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>;
    } else {
        body = (
            <Alert type="error" header="Unable to load the sheet"
                   action={<Button onClick={() => dasDispatch(ACTIONS.REFRESH_SHEET)}>Retry</Button>}>
                {error}
            </Alert>
        );
    }

    return (
        <DerivedAllocationSheetBuilderContext.Provider value={contextValue}>
            <Box padding={{ top: 'xxl' }}>
                <div className="multidimensionalAllocationBuilder" data-class="dasBuilder" data-view-mode={viewMode}>
                    {body}
                </div>
            </Box>
        </DerivedAllocationSheetBuilderContext.Provider>
    );
}
